/**
 * <TutorSessionNoticeDialog> — ce qu'une séance du masteur dit quand on la touche et qu'elle porte
 * déjà une demande (template).
 *
 * Les quatre boîtes de l'ancien `TutorLiveCardComponent.js` (son bouton « Gérer les RDV ») et de
 * `TutorHomeTabScreen.js`, une seule forme (`<ConfirmDialog>`) :
 *  - `late` — un retard signalé : ce que les élèves verront, et « Annuler la demande » ;
 *  - `substitutionPending` — une absence demandée, le remplacement pas encore accepté :
 *    « Annuler la demande » ;
 *  - `substitutionAccepted` — le remplacement est accepté : la boîte annonce, « Retour » seul ;
 *  - `cannotCancel` — l'annulation a été refusée (le remplacement tient à une absence sur
 *    plusieurs séances) : « Contacter le support ».
 * Une séance sans demande ne passe pas par ici : elle ouvre le déplacement de la séance.
 *
 * Coquille seule : les mots sont ici, la séance et les gestes arrivent en props. `cancelling` fait
 * tourner « Annuler la demande » (l'ancienne carte remplaçait son bouton par une roue).
 */
import React from "react"

import { ConfirmDialog } from "../../molecules/ConfirmDialog"
import { type UILiveSession } from "../../../ports/primary/ui/types"
import { formatDayTime } from "../../../ports/primary/ui/formatters/dateFormatter"

export type TutorSessionNotice = "late" | "substitutionPending" | "substitutionAccepted" | "cannotCancel"

export type TutorSessionNoticeDialogProps = {
    /** La boîte ouverte. `null` : fermée. */
    notice: TutorSessionNotice | null
    session: Pick<UILiveSession, "date"> | null
    /** Le retard signalé, en minutes (`late`). */
    lateMinutes?: number
    /** « Annuler la demande » (`late`, `substitutionPending`). */
    onCancelRequest?: () => void
    /** « Contacter le support » (`cannotCancel`). */
    onContactSupport?: () => void
    onClose: () => void
    testID?: string
}

type Texts = { title: string; message: string; confirm: string; danger: boolean; hideCancel: boolean }

function texts(notice: TutorSessionNotice, when: string, lateMinutes?: number): Texts {
    switch (notice) {
        case "late":
            return {
                title: `Retard de ${lateMinutes ?? 0} minutes signalé pour la séance ${when}`,
                message:
                    "Les élèves seront informés à l'heure de début du live, pour qu'ils commencent à travailler en attendant ton arrivée. Il faudra compenser ce retard en fin de séance.",
                confirm: "Annuler la demande",
                danger: true,
                hideCancel: false,
            }
        case "substitutionPending":
            return {
                title: `Demande d'absence envoyée pour la séance ${when}`,
                message: "En attente d'acceptation du remplacement par un tuteur.",
                confirm: "Annuler la demande",
                danger: true,
                hideCancel: false,
            }
        case "substitutionAccepted":
            return {
                title: `Demande d'absence acceptée pour la séance ${when}`,
                message: "Le remplacement a été accepté par un tuteur.",
                confirm: "Retour",
                danger: false,
                hideCancel: true,
            }
        case "cannotCancel":
            return {
                title: "Impossible d'annuler",
                message:
                    "Ce remplacement est lié à une demande d'absence sur plusieurs séances, et ne peut pas être annulé ici. Contacte l'équipe Masteur.",
                confirm: "Contacter le support",
                danger: false,
                hideCancel: false,
            }
    }
}

export function TutorSessionNoticeDialog(props: TutorSessionNoticeDialogProps) {
    const { notice, session, lateMinutes, onCancelRequest, onContactSupport, onClose, testID } = props
    const shown =
        notice != null
            ? texts(notice, session != null ? formatDayTime(session.date, true, "complement") : "", lateMinutes)
            : null

    const confirm = () => {
        if (notice === "late" || notice === "substitutionPending") onCancelRequest?.()
        else if (notice === "cannotCancel") onContactSupport?.()
        onClose()
    }

    return (
        <ConfirmDialog
            visible={shown != null}
            title={shown?.title ?? ""}
            message={shown?.message}
            confirmLabel={shown?.confirm}
            cancelLabel="Retour"
            danger={shown?.danger ?? false}
            hideCancel={shown?.hideCancel ?? false}
            onConfirm={confirm}
            onCancel={onClose}
            testID={testID}
        />
    )
}
